import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DEFAULT_ZOOM, SCENE_GRAPH_MODIFIED_DRAG_PAN_MULTIPLIER } from "./constants";
import {
  computeContentBounds,
  buildSceneGraphViewBox,
  computeSceneGraphAutoFitZoom,
} from "./viewport";
import {
  sceneGraphPanGestureTargetIsBackground,
  sceneGraphMiddlePanGestureTargetIsAllowed,
  isLikelyTrackpadPanWheel,
  getSceneGraphSvgPoint,
} from "./gestures";
import { downloadSceneGraphSvg } from "./exportSceneGraphSvg";
import { logger } from "../../utils/logger";

/**
 * Owns every scrap of viewport state for the scene-graph diagram: the
 * imperative pan/zoom refs, the auto-fit-on-first-render gating, the SVG
 * `viewBox` + content-group `transform` strings the JSX consumes, every
 * pointer/wheel/auxClick handler, and the gesture refs they share.
 *
 * Pan/zoom are deliberately stored in refs (not React state) because input
 * events fire many times per frame; a `useState` per event would cause a
 * full diagram re-render storm. Instead the hook mutates `panRef`/`zoomRef`
 * and rewrites the SVG attributes imperatively via
 * `applyDiagramViewportDom` / `applyDiagramPanTransformDom`. The unused
 * `setZoom`/`setPan` state setters exist only to trigger a render when
 * auto-fit kicks in (so the JSX `viewBox`/`transform` re-read the refs).
 *
 * The hook returns:
 *   - `svgRef`, `contentPanGroupRef` — attached by JSX
 *   - `viewBox`, `contentPanGroupTransform` — JSX render values
 *   - `diagramGestureTravelRef` — read by `handleNodeClick` so a click that
 *     came at the end of a pan drag is ignored
 *   - The five SVG event handlers + `handleDiagramAuxClick`
 *
 * Inputs:
 *   - `width`, `height`: SVG box size (component props)
 *   - `diagramData`: just `nodes` is consumed; the prop is the whole
 *     diagram so wheel and auto-fit can re-read on structural changes
 *   - `hasAnyMultiSelection`: a single boolean precomputed by the
 *     component so the pointerdown handler can clear the batch when a
 *     pan starts on empty canvas without the hook needing to know about
 *     each multi-selection bag
 *   - `clearBatchSelection`: invoked when a pan starts on empty canvas
 *     while a batch is active
 */
export function useSceneGraphViewport({
  width,
  height,
  diagramData,
  hasAnyMultiSelection,
  clearBatchSelection,
}) {
  const svgRef = useRef(null);
  /** Wraps all diagram geometry; pan updates this transform instead of `viewBox` for performance. */
  const contentPanGroupRef = useRef(null);

  // Unused `setZoom`/`setPan` reads — these only exist to force a render
  // when auto-fit fires. The actual zoom/pan values live in refs below so
  // input handlers can update them every frame without re-rendering.
  const [, setZoom] = useState(DEFAULT_ZOOM);
  const [, setPan] = useState({ x: 0, y: 0 });
  const hasInitializedViewportRef = useRef(false);

  /** Total pointer travel during the current left-drag gesture (for ignoring reparent picks after pan). */
  const diagramGestureTravelRef = useRef(0);

  const isDraggingRef = useRef(false);
  const dragPointerRef = useRef({ x: 0, y: 0 });
  const pendingPanDeltaRef = useRef({ x: 0, y: 0 });
  const panRafRef = useRef(0);
  /** When set, this pointer id owns the diagram pan gesture (touch / pen / mouse). */
  const panPointerIdRef = useRef(null);
  const panRef = useRef({ x: 0, y: 0 });
  const zoomRef = useRef(DEFAULT_ZOOM);

  // Cancel any outstanding pan rAF on unmount so we don't try to update the
  // detached SVG group.
  useEffect(() => {
    return () => {
      if (panRafRef.current) {
        cancelAnimationFrame(panRafRef.current);
        panRafRef.current = 0;
      }
    };
  }, []);

  // Auto-fit only on first paint (or after the graph empties and refills).
  // Subsequent structural edits keep the user's current pan/zoom so a tiny
  // tweak doesn't snap the camera back.
  useEffect(() => {
    if (!diagramData.nodes?.length) {
      setZoom(DEFAULT_ZOOM);
      setPan({ x: 0, y: 0 });
      zoomRef.current = DEFAULT_ZOOM;
      panRef.current = { x: 0, y: 0 };
      hasInitializedViewportRef.current = false;
      return;
    }

    if (hasInitializedViewportRef.current) return;

    const responsiveZoom = computeSceneGraphAutoFitZoom(diagramData.nodes, width, height);

    setZoom(responsiveZoom);
    setPan({ x: 0, y: 0 });
    zoomRef.current = responsiveZoom;
    panRef.current = { x: 0, y: 0 };
    hasInitializedViewportRef.current = true;
  }, [width, height, diagramData.nodes]);

  const contentBounds = useMemo(() => computeContentBounds(diagramData.nodes), [diagramData.nodes]);

  /** Sync zoom (viewBox) + pan (`contentPanGroupRef` translate). Call after wheel or when both may change. */
  const applyDiagramViewportDom = useCallback(() => {
    const svg = svgRef.current;
    const panG = contentPanGroupRef.current;
    const z = zoomRef.current;
    const p = panRef.current;
    if (!svg) return;
    if (!contentBounds) {
      svg.setAttribute("viewBox", `0 0 ${width} ${height}`);
      if (panG) panG.removeAttribute("transform");
      return;
    }
    svg.setAttribute(
      "viewBox",
      buildSceneGraphViewBox(contentBounds, { x: 0, y: 0 }, z, width, height)
    );
    if (panG) {
      panG.setAttribute("transform", `translate(${p.x / z}, ${p.y / z})`);
    }
  }, [contentBounds, width, height]);

  /** Pan-only DOM update while dragging — avoids remapping the entire SVG via `viewBox`. */
  const applyDiagramPanTransformDom = useCallback(() => {
    const panG = contentPanGroupRef.current;
    if (!panG || !contentBounds) return;
    const z = zoomRef.current;
    const p = panRef.current;
    panG.setAttribute("transform", `translate(${p.x / z}, ${p.y / z})`);
  }, [contentBounds]);

  // ─── viewBox (zoom) + pan transform ───────────────────────────────────
  // Pan is applied on `contentPanGroupRef` so pointer-pan can update one
  // `<g>` transform per frame instead of `viewBox` (which is costly for
  // huge graphs). Always derive from refs so wheel/drag can update the
  // viewport imperatively without a React render per input event — stale
  // useMemo here would snap the view back on unrelated re-renders.
  const viewBox = buildSceneGraphViewBox(
    contentBounds,
    { x: 0, y: 0 },
    zoomRef.current,
    width,
    height
  );
  const contentPanGroupTransform =
    contentBounds != null
      ? `translate(${panRef.current.x / zoomRef.current}, ${panRef.current.y / zoomRef.current})`
      : undefined;

  // ─── pan/zoom event handlers ──────────────────────────────────────────
  const finalizeDiagramPanGesture = useCallback(() => {
    if (!isDraggingRef.current) return;
    isDraggingRef.current = false;
    panPointerIdRef.current = null;
    if (svgRef.current) svgRef.current.style.cursor = "grab";
    if (panRafRef.current) {
      cancelAnimationFrame(panRafRef.current);
      panRafRef.current = 0;
    }
    // Flush any leftover pending delta so the final frame doesn't get lost
    // (rAF that hadn't fired yet was just cancelled above).
    const { x, y } = pendingPanDeltaRef.current;
    pendingPanDeltaRef.current = { x: 0, y: 0 };
    if (x !== 0 || y !== 0) {
      panRef.current = {
        x: panRef.current.x + x,
        y: panRef.current.y + y,
      };
    }
    applyDiagramViewportDom();
    wheelStateRef.current.pan = panRef.current;
  }, [applyDiagramViewportDom]);

  const handleDiagramPointerDown = useCallback(
    (event) => {
      if (!event.isPrimary) return;
      const isMouse = event.pointerType === "mouse";
      const isMiddleMousePan = isMouse && event.button === 1;
      const isLeftMousePan = !isMouse || event.button === 0;
      if (isMouse && !isLeftMousePan && !isMiddleMousePan) return;

      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target.closest("[data-scene-selectable-node='true']")) {
        diagramGestureTravelRef.current = 0;
        return;
      }

      // Starting a left-click pan on empty canvas while a multi-selection
      // is active doubles as a "deselect everything" gesture; the click
      // never lands on a selectable node anyway.
      if (
        isLeftMousePan &&
        (event.pointerType !== "mouse" || event.buttons === 1 || event.button === 0) &&
        !target.closest("[data-scene-selectable-node='true']") &&
        hasAnyMultiSelection
      ) {
        clearBatchSelection();
      }

      const canStartPan = isMiddleMousePan
        ? sceneGraphMiddlePanGestureTargetIsAllowed(target)
        : sceneGraphPanGestureTargetIsBackground(target);
      if (!canStartPan) return;

      event.preventDefault();
      event.stopPropagation();
      diagramGestureTravelRef.current = 0;
      dragPointerRef.current = { x: event.clientX, y: event.clientY };
      isDraggingRef.current = true;
      panPointerIdRef.current = event.pointerId;
      const svgEl = event.currentTarget;
      if (svgRef.current) svgRef.current.style.cursor = "grabbing";
      if (svgEl && typeof svgEl.setPointerCapture === "function") {
        try {
          svgEl.setPointerCapture(event.pointerId);
        } catch {
          /* noop */
        }
      }
    },
    [clearBatchSelection, hasAnyMultiSelection]
  );

  // Middle-click would otherwise open the browser's auto-scroll mode; we
  // already used the same button to initiate a pan in pointerdown.
  const handleDiagramAuxClick = useCallback((event) => {
    if (event.button !== 1) return;
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleDiagramPointerMove = useCallback(
    (event) => {
      if (panPointerIdRef.current !== event.pointerId) return;
      if (!isDraggingRef.current) return;
      event.preventDefault();
      const dx = event.clientX - dragPointerRef.current.x;
      const dy = event.clientY - dragPointerRef.current.y;
      // Alt/Shift slow the pan for fine-grained positioning.
      const panMultiplier =
        event.altKey || event.shiftKey ? SCENE_GRAPH_MODIFIED_DRAG_PAN_MULTIPLIER : 1;
      dragPointerRef.current = { x: event.clientX, y: event.clientY };
      diagramGestureTravelRef.current += Math.abs(dx) + Math.abs(dy);
      pendingPanDeltaRef.current = {
        x: pendingPanDeltaRef.current.x + dx * panMultiplier,
        y: pendingPanDeltaRef.current.y + dy * panMultiplier,
      };
      // Coalesce many pointermove events into one rAF-driven DOM update.
      if (!panRafRef.current) {
        panRafRef.current = requestAnimationFrame(() => {
          const { x, y } = pendingPanDeltaRef.current;
          pendingPanDeltaRef.current = { x: 0, y: 0 };
          panRafRef.current = 0;
          if (x !== 0 || y !== 0) {
            panRef.current = {
              x: panRef.current.x + x,
              y: panRef.current.y + y,
            };
            applyDiagramPanTransformDom();
          }
        });
      }
    },
    [applyDiagramPanTransformDom]
  );

  const handleDiagramPointerUpOrCancel = useCallback(
    (event) => {
      if (panPointerIdRef.current !== event.pointerId) return;
      const svg = event.currentTarget;
      const id = event.pointerId;
      // releasePointerCapture fires lostpointercapture, which calls
      // finalize for us. If the call throws (or there was no capture to
      // release), finalize directly so we don't leak the dragging state.
      if (svg && typeof svg.hasPointerCapture === "function" && svg.hasPointerCapture(id)) {
        try {
          svg.releasePointerCapture(id);
        } catch {
          finalizeDiagramPanGesture();
        }
      } else {
        finalizeDiagramPanGesture();
      }
    },
    [finalizeDiagramPanGesture]
  );

  const handleDiagramLostPointerCapture = useCallback(() => {
    finalizeDiagramPanGesture();
  }, [finalizeDiagramPanGesture]);

  // Keep a ref with the latest zoom/pan/size/nodes so the wheel listener
  // (registered once per SVG mount) can always read current values without
  // being re-registered on every render.
  const wheelStateRef = useRef({
    zoom: zoomRef.current,
    pan: panRef.current,
    width,
    height,
    nodes: diagramData.nodes,
  });
  wheelStateRef.current = {
    zoom: zoomRef.current,
    pan: panRef.current,
    width,
    height,
    nodes: diagramData.nodes,
  };
  const isDiagramEmpty = diagramData.nodes.length === 0;

  const recenterSceneGraphView = useCallback(() => {
    if (!diagramData.nodes?.length) return;
    const nextZoom = computeSceneGraphAutoFitZoom(diagramData.nodes, width, height);
    const nextPan = { x: 0, y: 0 };
    zoomRef.current = nextZoom;
    panRef.current = nextPan;
    wheelStateRef.current = {
      ...wheelStateRef.current,
      zoom: nextZoom,
      pan: nextPan,
    };
    hasInitializedViewportRef.current = true;
    setZoom(nextZoom);
    setPan(nextPan);
    applyDiagramViewportDom();
  }, [applyDiagramViewportDom, diagramData.nodes, height, width]);

  const downloadSceneGraphViewSvg = useCallback(() => {
    try {
      downloadSceneGraphSvg(svgRef.current);
    } catch (error) {
      logger.error("[scene-graph] failed to export SVG:", error);
      throw error;
    }
  }, []);

  useEffect(() => {
    const el = svgRef.current;
    if (!el) return;
    let lastTrackpadWheelAt = -Infinity;
    const onWheel = (e) => {
      e.preventDefault();
      // width/height live on the ref for symmetry with `applyDiagramViewportDom`,
      // but the wheel math itself doesn't need them (the canvas size is
      // captured in the SVG element's current viewBox, which we transform via
      // `applyDiagramViewportDom`).
      const { zoom, pan, nodes } = wheelStateRef.current;
      let newZoom = zoom;
      let newPan;

      if (isLikelyTrackpadPanWheel(e, lastTrackpadWheelAt)) {
        // Two-finger trackpad scroll → pan (matches how every map app
        // behaves). Track the timestamp so the next wheel event in the
        // session keeps treating us as a trackpad even if a single sample
        // looks ambiguous.
        lastTrackpadWheelAt = performance.now();
        newPan = {
          x: pan.x - (e.deltaX || 0),
          y: pan.y - (e.deltaY || 0),
        };
      } else if (e.shiftKey || e.altKey) {
        // Modifier-held mouse wheel pans on one axis.
        const deltaX = e.shiftKey ? (Math.abs(e.deltaX) > 0 ? e.deltaX : e.deltaY) : 0;
        const deltaY = e.altKey ? (Math.abs(e.deltaY) > 0 ? e.deltaY : e.deltaX) : 0;
        newPan = {
          x: pan.x - deltaX,
          y: pan.y - deltaY,
        };
      } else {
        // Plain mouse wheel = zoom toward cursor.
        const factor = e.deltaY > 0 ? 0.9 : 1.1;
        newZoom = Math.max(0.2, Math.min(5, zoom * factor));
        const actualScale = newZoom / zoom;

        if (!nodes.length) {
          zoomRef.current = newZoom;
          wheelStateRef.current = { ...wheelStateRef.current, zoom: newZoom };
          applyDiagramViewportDom();
          return;
        }

        const svgPoint = getSceneGraphSvgPoint(el, e.clientX, e.clientY);
        if (!svgPoint) {
          zoomRef.current = newZoom;
          wheelStateRef.current = { ...wheelStateRef.current, zoom: newZoom };
          applyDiagramViewportDom();
          return;
        }

        // `svgPoint` is in the current viewBox coordinate system, before
        // the content group pan transform. Convert it back to the
        // unzoomed graph basis used by `panRef`, then keep that graph
        // point under the cursor.
        const wx0 = svgPoint.x * zoom;
        const wy0 = svgPoint.y * zoom;
        const newPanX = (1 - actualScale) * wx0 + actualScale * pan.x;
        const newPanY = (1 - actualScale) * wy0 + actualScale * pan.y;
        newPan = { x: newPanX, y: newPanY };
      }

      panRef.current = newPan;
      zoomRef.current = newZoom;
      wheelStateRef.current = {
        ...wheelStateRef.current,
        zoom: newZoom,
        pan: newPan,
      };
      applyDiagramViewportDom();
    };
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => el.removeEventListener("wheel", onWheel);
    // Re-run when bounds math changes or when the SVG is remounted (empty
    // graph toggles the SVG in/out of the DOM, so svgRef points at a new
    // element).
  }, [applyDiagramViewportDom, isDiagramEmpty]);

  return {
    svgRef,
    contentPanGroupRef,
    viewBox,
    contentPanGroupTransform,
    diagramGestureTravelRef,
    handleDiagramPointerDown,
    handleDiagramPointerMove,
    handleDiagramPointerUpOrCancel,
    handleDiagramLostPointerCapture,
    handleDiagramAuxClick,
    recenterSceneGraphView,
    downloadSceneGraphViewSvg,
  };
}
